/*
  CatchFish bundle hero.
  - Prices and the bundle total come from real Shopify variant data.
  - "Add both to cart" posts both variants to Shopify's own cart API, so the
    cart drawer, analytics and checkout behave exactly as elsewhere.
  - Each product's "3D" button opens that product's shared viewer (see
    CfBundleViewer below). Nothing loads until a button is pressed.
*/
/* "3D" buttons: each opens its own product in the SAME viewer engine and with the
   SAME per-product configuration as that product's page (snippets/cf-3d-config.liquid):
   a real GLB via assets/cf-3d.js once one is live, otherwise the real-photo viewer
   (assets/cf-photoview.js). Nothing is downloaded until a button is pressed. */
const cfLoadScript = (() => {
  const cache = new Map();
  return (src) => {
    if (!cache.has(src)) {
      cache.set(
        src,
        new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = src;
          script.async = true;
          script.onload = resolve;
          script.onerror = () => {
            cache.delete(src);
            reject(new Error(`Could not load ${src}`));
          };
          document.head.appendChild(script);
        })
      );
    }
    return cache.get(src);
  };
})();

class CfBundleViewer {
  constructor(root) {
    this.root = root;
    this.el = root.querySelector('[data-viewer]');
    if (!this.el) return;
    this.dialog = this.el.querySelector('[data-viewer-dialog]');
    this.stage = this.el.querySelector('[data-viewer-stage]');
    this.titleTarget = this.el.querySelector('[data-viewer-title]');
    this.subTarget = this.el.querySelector('[data-viewer-sub]');
    this.onKeydown = this.onKeydown.bind(this);
    this.el.querySelectorAll('[data-viewer-close], [data-viewer-close-button]').forEach((b) => b.addEventListener('click', () => this.close()));
  }

  config(n, selector) {
    const article = this.root.querySelector(`.cf-bundle__object[data-product="${n}"]`);
    const script = article && article.querySelector(selector);
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return null;
    }
  }

  async open(n, trigger) {
    if (!this.el || this.busy || !this.el.hidden) return;
    const config3d = this.config(n, 'script[data-cf-3d-config]');
    const configPhoto = this.config(n, 'script[data-cf-pv-config]');
    if (!config3d && !configPhoto) return;
    const input = this.root.querySelector(`[data-variant-select="${n}"]`);
    const variantId = input ? input.value : null;

    this.trigger = trigger;
    this.scrollY = window.scrollY;
    if (this.titleTarget) this.titleTarget.textContent = trigger.dataset.viewerTitle || '';
    if (this.subTarget) this.subTarget.textContent = trigger.dataset.viewerSub || '';
    this.el.hidden = false;
    document.body.classList.add('overflow-hidden');
    document.addEventListener('keydown', this.onKeydown);
    this.busy = true;
    trigger.classList.add('is-loading');
    const onClose = () => {
      this.viewer = null;
      this.close();
    };
    try {
      if (config3d && config3d.assets) {
        // The 3D engine uses CfGallery.resolve3d / loadScript from the product-page script.
        await cfLoadScript(this.el.dataset.pdpScript);
        await cfLoadScript(config3d.assets.engine);
        if (window.CfGallery && window.CfGallery.resolve3d(config3d, variantId)) {
          this.viewer = window.Cf3d.open(this.stage, config3d, { variantId, onClose });
        }
      }
      if (!this.viewer && configPhoto && configPhoto.assets) {
        await cfLoadScript(configPhoto.assets.engine);
        this.viewer = await window.CfPhotoView.open(this.stage, configPhoto, {
          variantId,
          onClose,
          host: this.stage.querySelector('[data-cf-pv-host]'),
          button: trigger,
          back: false,
          fullscreen: false,
        });
      }
    } catch (error) {
      console.warn('[CatchFish bundle viewer]', error);
    } finally {
      this.busy = false;
      trigger.classList.remove('is-loading');
    }
    if (!this.viewer) this.close();
  }

  close() {
    if (!this.el || this.el.hidden) return;
    const viewer = this.viewer;
    this.viewer = null;
    if (viewer) viewer.close({ focus: false });
    this.el.hidden = true;
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keydown', this.onKeydown);
    // Return to exactly the same place in the bundle.
    if (typeof this.scrollY === 'number' && Math.abs(window.scrollY - this.scrollY) > 1) window.scrollTo(0, this.scrollY);
    if (this.trigger) this.trigger.focus({ preventScroll: true });
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;
    /* keep focus inside the dialog */
    const focusable = this.dialog.querySelectorAll('button:not([disabled]):not([hidden]), [href], select, [tabindex]:not([tabindex="-1"])');
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('cf-bundle')) {
  customElements.define(
    'cf-bundle',
    class CfBundle extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.selects = Array.from(this.querySelectorAll('[data-variant-select]'));
        this.totalTarget = this.querySelector('[data-bundle-total]');
        this.addButton = this.querySelector('[data-bundle-add]');
        this.ctaText = this.querySelector('[data-bundle-cta-text]');
        this.errorTarget = this.querySelector('[data-bundle-error]');
        this.strings = {
          cta: this.ctaText ? this.ctaText.textContent.trim() : 'Add both to cart',
          adding: this.dataset.addingText || null,
        };

        const totalsScript = document.querySelector(`[data-bundle-totals="${this.sectionId}"]`);
        try {
          this.totals = totalsScript ? JSON.parse(totalsScript.textContent) : {};
        } catch (e) {
          this.totals = {};
        }

        this.selects.forEach((select) => {
          if (select.tagName !== 'SELECT') return;
          select.addEventListener('change', () => {
            this.syncProduct(select);
            this.syncTotal();
          });
        });

        if (this.addButton) this.addButton.addEventListener('click', () => this.addBoth());

        this.bindImageFallbacks();
        this.viewer = new CfBundleViewer(this);
        this.querySelectorAll('[data-open-viewer]').forEach((button) => {
          button.addEventListener('click', () => this.viewer.open(button.dataset.openViewer, button));
        });

        this.syncTotal();
      }

      /* keep the shown price and image in step with the chosen variant */
      syncProduct(select) {
        const n = select.dataset.variantSelect;
        const option = select.options[select.selectedIndex];
        if (!option) return;
        const priceTarget = this.querySelector(`[data-product-price="${n}"]`);
        if (priceTarget && option.dataset.price) priceTarget.textContent = option.dataset.price;
        const image = this.querySelector(`[data-bundle-image="${n}"] img`);
        if (image && option.dataset.image) {
          if (option.dataset.fallback) image.dataset.fallback = option.dataset.fallback;
          image.src = option.dataset.image;
          image.removeAttribute('srcset');
        }
      }

      /* a cut-out may not exist for a newly added variant: show the Shopify photo */
      bindImageFallbacks() {
        this.querySelectorAll('[data-bundle-image] img').forEach((img) => {
          img.addEventListener('error', () => {
            const fallback = img.dataset.fallback;
            if (fallback && img.src !== fallback) img.src = fallback;
          });
        });
      }

      get selectedIds() {
        return this.selects.map((select) => String(select.value));
      }

      /* the total is a real sum of the two Shopify variant prices */
      syncTotal() {
        const key = this.selectedIds.join(':');
        if (this.totalTarget && this.totals[key]) this.totalTarget.innerHTML = this.totals[key];
        const unavailable = this.selects.some((select) => {
          if (select.tagName !== 'SELECT') return false;
          const option = select.options[select.selectedIndex];
          return !option || option.disabled;
        });
        if (this.addButton) this.addButton.disabled = unavailable;
      }

      setError(message) {
        if (!this.errorTarget) return;
        this.errorTarget.textContent = message || '';
        this.errorTarget.hidden = !message;
      }

      async addBoth() {
        const ids = this.selectedIds.filter(Boolean);
        if (ids.length < 2 || this.addButton.disabled) return;
        this.setError('');
        const label = this.ctaText ? this.ctaText.textContent : '';
        this.addButton.disabled = true;
        this.addButton.setAttribute('aria-busy', 'true');
        if (this.ctaText && this.dataset.addingText) this.ctaText.textContent = this.dataset.addingText;

        const cartDrawer = document.querySelector('cart-drawer');
        const body = {
          items: ids.map((id) => ({ id: Number(id), quantity: 1 })),
        };
        if (cartDrawer) {
          body.sections = ['cart-drawer', 'cart-icon-bubble'];
          body.sections_url = window.location.pathname;
        }

        try {
          const response = await fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify(body),
          });
          const data = await response.json();
          if (!response.ok || data.status) {
            this.setError((data && data.description) || this.dataset.errorText || '');
            return;
          }
          if (cartDrawer && data.sections) {
            cartDrawer.classList.remove('is-empty');
            cartDrawer.renderContents(data);
          } else {
            window.location = (window.routes && window.routes.cart_url) || '/cart';
            return;
          }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cf-bundle', cartData: data });
          }
          if (this.ctaText && this.dataset.addedText) {
            this.ctaText.textContent = this.dataset.addedText;
            setTimeout(() => {
              this.ctaText.textContent = label;
            }, 2500);
          }
        } catch (e) {
          this.setError(this.dataset.errorText || '');
        } finally {
          this.addButton.removeAttribute('aria-busy');
          this.syncTotal();
          if (this.ctaText && !this.dataset.addedText) this.ctaText.textContent = label;
        }
      }
    }
  );
}
